export interface SlackEventShape {
  id: number;
  notify_scope: "ops" | "eng";
  notify_channel_name: string;
  slackable_channel_name: string;
  slackable_title: string;
  slackable_title_link: string;
  slackable_title_eng: string;
  slackable_lead_number: string;
  message_text: string;
  sender_username: string;
  sender_icon_url: string;
  attachment_author_name: string;
  attachment_author_link: string;
  attachment_text: string;
  attachment_fallback: string;
  attachment_title: string;
  attachment_title_link: string;
  attachment_color: string;
  attachment_fields: object;
  created_at: string;
  updated_at: string;
}

interface AppraisalFields {
  "Requested Date"?: string;
  "Expected Date"?: string;
  "Approved Date"?: string;
}

interface ArchiveFields {
  /* none */
}

interface CloneFields {
  cloned_from?: string;
  cloned_as_of?: string;
}

interface ConditionFields {
  condition_id?: string;
  condition?: string;
  borrower_id?: string;
  previous_status?: string;
  current_status?: string;
}

interface DeclarationsFields {
  BorrowerEntity?: string;
  Diff?: string;
}

interface DocmagicFields {
  /* none */
}

interface FeeChangeFields {
  actual_amount?: string;
  estimated_amount?: string;
  change_type: string;
  operator_name?: string;
}

interface HurdleNoteFields {
  note?: string;
  pinned?: boolean;
}

interface KahunaMessageFields {
  messageId: string;
  eventType: string;
  newDate?: string;
  leadId?: string;
  source?: string;
  description?: string;
  attributes?: string;
  error?: string;
}

interface LobLetterFields {
  /* none */
}

interface PointsAndCreditsFields {
  displayed_loan_rate: string;
  daily_base_rate: string;
  price_delta: string;
  amount: string;
  points_or_credits: string;
}

interface SandboxFields {
  description: string;
  sandbox_request: string;
  sandbox_response: string;
}

interface SSNFields {
  borrower?: string;
}

interface WorksheetFields {
  /* none */
}

interface FieldUpdateFields {
  original_value: string;
  new_value: string;
}

export interface TRUSEventShape {
  id: number;
  scope: "ops" | "eng";
  kind:
    | AppraisalKinds
    | ArchiveKinds
    | CloneKinds
    | ConditionKinds
    | ContactUpdatedKinds
    | DeclarationsKinds
    | DocmagicKinds
    | FeeChangeKinds
    | FieldUpdateKinds
    | HurdleNoteKinds
    | KahunaMessageKinds
    | LobLetterKinds
    | PointsAndCreditsKinds
    | SandboxKinds
    | SSNKinds
    | WorksheetKinds;
  author_email: string;
  author_first_name: string;
  author_last_name: string;
  fields:
    | (AppraisalFields &
        ArchiveFields &
        CloneFields &
        ConditionFields &
        DeclarationsFields &
        DocmagicFields &
        FeeChangeFields &
        FieldUpdateFields &
        HurdleNoteFields &
        KahunaMessageFields &
        LobLetterFields &
        PointsAndCreditsFields &
        SandboxFields &
        SSNFields &
        WorksheetFields)
    | null;
  created_at: string;
  updated_at: string;
}

export interface AddEventShape {
  appId: number;
  kind: HurdleNoteKinds; // right now, only hurdle note kinds can be created on front-end
  fields: {};
}

export enum AppraisalKinds {
  ReportSent = "appraisal_report_sent",
  ReportDelivered = "appraisal_report_delivered",
  ReportRequested = "appraisal_report_requested",
  ReportExpected = "appraisal_report_expected",
  ReportApproved = "appraisal_report_approved",
}

export enum ArchiveKinds {
  LoanArchived = "loan_archived",
  LoanUnArchived = "loan_un_archived",
}

export enum CloneKinds {
  Clone = "clone",
}

export enum ConditionKinds {
  Update = "condition_update",
}

export enum ContactUpdatedKinds {
  ContactAssigned = "contact_assigned",
}

export enum DeclarationsKinds {
  Update = "declarations_update",
}

export enum DocmagicKinds {
  CompleteClicksign = "docmagic_complete_clicksign",
  CompleteEsign = "docmagic_complete_esign",
  CompleteSigreq = "docmagic_complete_sigreq",
  ConsentReapplied = "docmagic_consent_reapplied",
  ConsentWithdraw = "docmagic_consent_withdraw",
  CreateSigreq = "docmagic_create_sigreq",
  DeliverDocument = "docmagic_deliver_document",
  DeliverPaperCopy = "docmagic_deliver_paper_copy",
  DeliverReport = "docmagic_deliver_report",
  ExpireSigreq = "docmagic_expire_sigreq",
  InksignFaxReceived = "docmagic_inksign_fax_received",
  InksignUploadReceived = "docmagic_inksign_upload_received",
  NoteToSender = "docmagic_note_to_sender",
  ReminderIncomplete = "docmagic_reminder_incomplete",
  RespaComplete = "docmagic_respa_complete",
  SendSigreqInvite = "docmagic_send_sigreq_invite",
  StartEsign = "docmagic_start_esign",
  ViewSigreq = "docmagic_view_sigreq",
}

export enum FeeChangeKinds {
  LoanOrigination = "fee_change_loan_origination",
}

export enum HurdleNoteKinds {
  AppraisalReport = "note_hurdle_appraisal_report",
  AssetSummary = "note_hurdle_asset_summary",
  CashCloseSummary = "note_hurdle_cash_close_summary",
  CQMBasicInfoReview = "note_hurdle_cqm_basic_info_review",
  CQMDocMagicReview = "note_hurdle_cqm_docmagic_review",
  CQMMiscInfoReview = "note_hurdle_cqm_misc_info_review",
  ClosingExplanations = "note_hurdle_closing_explanations",
  ClosingSummary = "note_hurdle_closing_summary",
  Conditions = "note_hurdle_conditions",
  ConflictOfInterest = "note_hurdle_conflict_of_interest",
  ContactInfo = "note_hurdle_contact_info",
  CreditReportPull = "note_hurdle_credit_report_pull",
  CreditSummary = "note_hurdle_credit_summary",
  Declarations = "note_hurdle_declarations",
  DesktopOriginator = "note_hurdle_desktop_originator",
  DisclosureAPRMismatch = "note_hurdle_disclosure_apr_mismatch",
  DisclosureEntities = "note_hurdle_disclosure_entities",
  DisclosureRegeneration = "note_hurdle_disclosure_regeneration",
  DisclosureRequestLintResults = "note_hurdle_disclosure_request_lint_results",
  DocmagicWarnings = "note_hurdle_docmagic_warnings",
  Documents = "note_hurdle_documents",
  EmailConfirmed = "note_hurdle_email_confirmed",
  Employment = "note_hurdle_employment",
  Escrows = "note_hurdle_escrows",
  Explanations = "note_hurdle_explanations",
  Fees = "note_hurdle_fees",
  Flags = "note_hurdle_flags",
  FloodCertification = "note_hurdle_flood_certification",
  Fraud = "note_hurdle_fraud",
  Funding = "note_hurdle_funding",
  HazardInsurance = "note_hurdle_hazard_insurance",
  History = "note_hurdle_history",
  IncomeDocumentsReview = "note_hurdle_income_documents_review",
  IncomeLOE = "note_hurdle_income_loe",
  IncomeSummary = "note_hurdle_income_summary",
  LoanFileWorksheet = "note_hurdle_loan_file_worksheet",
  LoanProduct = "note_hurdle_loan_product",
  MortgageInsuranceSummary = "note_hurdle_mortgage_insurance_summary",
  // OtherIncomeCashFlowAnalysis = "note_hurdle_other_income_cashflow_analysis",
  OwnerOccupancy = "note_hurdle_owner_occupancy",
  PersonalInfo = "note_hurdle_personal_info",
  Prepaids = "note_hurdle_prepaids",
  PropertyCondition = "note_hurdle_property_condition",
  PropertyType = "note_hurdle_property_type",
  ResidualIncome = "note_hurdle_residual_income",
  SalariedIncomeAnalysis = "note_hurdle_salaried_income_analysis",
  SecondLien = "note_hurdle_second_lien",
  SpecialSituations = "note_hurdle_special_situations",
  SubjectPropertyReport = "note_hurdle_subject_property_report",
  TaxReturnIncomeAnalysis = "note_hurdle_tax_return_income_analysis",
  TaxTranscripts = "note_hurdle_tax_transcripts",
  TaxTranscriptsMatchIngestedReturns = "note_hurdle_tax_transcripts_match_ingested_returns",
  Title = "note_hurdle_title",
  TitleCommitment = "note_hurdle_title_commitment",
  TransactionTerms = "note_hurdle_transaction_terms",
  UnderwritingHub = "note_hurdle_underwriting_hub",
  UnderwritingSummary = "note_hurdle_underwriting_summary",
  VerbalVerificationOfEmployment = "note_hurdle_verbal_verification_of_employment",
  Vesting = "note_hurdle_vesting",
  WrittenVerificationOfEmployment = "note_hurdle_written_verification_of_employment",
}

export enum KahunaMessageKinds {
  SendFailure = "kahuna_message_send_failure",
  Sent = "kahuna_message_sent",
  Received = "kahuna_message_received",
}

export enum LobLetterKinds {
  Created = "lob_letter_created",
  RenderedPDF = "lob_letter_rendered_pdf",
  RenderedThumbnails = "lob_letter_rendered_thumbnails",
  Deleted = "lob_letter_deleted",
  Mailed = "lob_letter_mailed",
  InTransit = "lob_letter_in_transit",
  InLocalArea = "lob_letter_in_local_area",
  ProcessedForDelivery = "lob_letter_processed_for_delivery",
  Rerouted = "lob_letter_rerouted",
  ReturnedToSender = "lob_letter_returned_to_sender",
}

export enum ReportingEventKinds {
  UnderwritingDismissed = "reporting_underwriting_dismissed",
}

export enum PointsAndCreditsKinds {
  RateSelection = "points_and_credits_rate_selection",
}

export enum SandboxKinds {
  ReworkRequest = "sandbox_rework_request",
}

export enum SSNKinds {
  Update = "ssn_update",
}

export enum WorksheetKinds {
  EditEscrows = "worksheet_edit_escrows",
  EditPrepaids = "worksheet_edit_prepaids",
}

export enum FieldUpdateKinds {
  FieldUpdateExpectedGrossMonthlyRentalIncome = "field_update_expected_gross_monthly_rental_income",
}

export const LOAD_EVENTS_PENDING = "@Events/LOAD_EVENTS_PENDING";
export const LOAD_EVENTS_FAILURE = "@Events/LOAD_EVENTS_FAILURE";
export const LOAD_EVENTS_SUCCESS = "@Events/LOAD_EVENTS_SUCCESS";
export const ADD_EVENT_PENDING = "@Events/ADD_EVENT_PENDING";
export const ADD_EVENT_FAILURE = "@Events/ADD_EVENT_FAILURE";
export const ADD_EVENT_SUCCESS = "@Events/ADD_EVENT_SUCCESS";

interface LoadEventsPendingAction {
  type: typeof LOAD_EVENTS_PENDING;
}

interface LoadEventsFailureAction {
  type: typeof LOAD_EVENTS_FAILURE;
  error: string;
}

interface LoadEventsSuccessAction {
  type: typeof LOAD_EVENTS_SUCCESS;
  payload: {
    slackEvents: SlackEventShape[];
    TRUSEvents: TRUSEventShape[];
  };
}

interface AddEventPendingAction {
  type: typeof ADD_EVENT_PENDING;
}

interface AddEventFailureAction {
  type: typeof ADD_EVENT_FAILURE;
  error: string;
}

interface AddEventSuccessAction {
  type: typeof ADD_EVENT_SUCCESS;
  payload: {
    TRUSEvent: TRUSEventShape;
  };
}

export type EventActionTypes =
  | LoadEventsPendingAction
  | LoadEventsFailureAction
  | LoadEventsSuccessAction
  | AddEventPendingAction
  | AddEventFailureAction
  | AddEventSuccessAction;
